import React, { useRef, useState } from 'react';
import { Search } from '@americanexpress/dls-react';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { withRouter } from '@americanexpress/one-app-router';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useTranslatedAirportResults } from '@/hooks/airportHooks/airportsHelperHooks';
import {
  useCommonContent,
  useOneCmsLocaleMinimumSearchLength,
  useOneCmsLocaleSearchObj,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import styles from './header/GlobalHeader.module.scss';
import AirportLinkWithImage from './AirportLinkWithImage';
import LFLink from './LFLink';
import ChevronLeft from '../icons/ChevronLeft';

const TCNSearchBox = ({ toggleFindAirportMenu }) => {
  const [query, setQuery] = useState('');
  const [routedAirport] = useState('');
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const isUsMarket = !locale || locale.toLocaleLowerCase() === 'en-us';
  const { featuredAirports, header } = useCommonContent();
  const searchCopy = useOneCmsLocaleSearchObj();

  const { customTrack } = useAnalyticsContext();

  const inputRef = useRef(null);
  const airportsMatchingQuery = useTranslatedAirportResults(query);
  const minQueryLength = useOneCmsLocaleMinimumSearchLength();

  const onFocus = () => {
    if (query === routedAirport) {
      setQuery('');
    }
  };

  const onBlur = (e) => {
    if (query === '') {
      setQuery(routedAirport);
    }
    customTrack({ event: e, detail: query });
  };

  const handleOnChange = (event) => {
    setQuery(event.target.value);
  };

  const airportsList =
    query.length >= minQueryLength
      ? airportsMatchingQuery
      : featuredAirports.airports;

  return (
    <div className="tw-mx-auto tw-flex tw-flex-col tw-items-start tw-px-10 tw-pb-10 tw-pt-8 md_tw-flex-row md_tw-justify-between lg_tw-max-w-360">
      <div
        className={cn(
          styles.searchBar,
          'tw-mb-4 tw-w-full md_tw-mb-0 md_tw-w-80'
        )}
      >
        <h1 className="tw-heading-xs-bold tw-mb-4 tw-font-bold tw-text-gray-500">
          {header.searchTabTitle}
        </h1>
        <Search
          id="findLounges"
          onBlur={onBlur}
          onFocus={onFocus}
          onClearSearch={() => setQuery('')}
          value={query || ''}
          containerRef={inputRef}
          onChange={handleOnChange}
          isOpen={false}
          placeholder={searchCopy.placeholder}
        />
      </div>
      <div className="tw-hidden tw-w-px tw-self-stretch tw-bg-blue-deep-tint-700-t12 md_tw-flex" />
      <div>
        <h1 className="tw-heading-xs-bold tw-mb-4 tw-font-bold tw-text-gray-500">
          {query.length >= minQueryLength
            ? `Results for "${query}"`
            : featuredAirports.header}
        </h1>
        <ul className="tw-my-6 tw-grid tw-gap-6 sm_tw-grid-cols-2 lg_tw-grid-cols-3">
          {airportsList.map((airport) => {
            return (
              <li key={airport.iata} className="tw-max-w-80">
                <AirportLinkWithImage
                  airport={airport}
                  background="dark"
                  onClickHandler={toggleFindAirportMenu}
                />
              </li>
            );
          })}
        </ul>
        {isUsMarket && (
          <div>
            <LFLink
              to={`${product}/all-airports/`}
              className="tw-blue-bright-400 tw-my-2dot5 tw-inline-flex tw-items-center tw-font-medium tw-text-blue-bright-400"
              element="all-airports-link"
              onClickHandler={toggleFindAirportMenu}
            >
              {featuredAirports?.link}
              <ChevronLeft className="tw-blue-bright-400 tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
            </LFLink>
          </div>
        )}
      </div>
    </div>
  );
};

const hocChain = compose(withRouter);

export default hocChain(TCNSearchBox);
